import $ = require("jquery");

import websiteData = require("diy/util/websiteData");

import ConfigFactory from "./utils/ConfigFactory";
import {NotificationState, NotificationStateList} from "./definitions/Config";

export interface NotificationRestData {
    type: number;
    category?: number;
    severity?: number;
    payload?: any;
    sticky?: number;
    read: number;
    dismissed: number;
    deleted: number;
}

export default class RestApi {
    private static restUrl = `/rest/websites/${websiteData.getId()}/notifications`;

    /**
     * Creates a Notification with a given state object
     *
     * @param   {NotificationState} notificationState
     * @returns {Promise<{}>}
     */
    static createNotification(notificationState: NotificationState): Promise<{}> {
        return new Promise((resolve, reject) => {
            try {
                $.ajax({
                    method: "POST",
                    url: this.restUrl,
                    data: JSON.stringify(this.getNotificationData(notificationState)),
                    contentType: "application/vnd.oneandone.diy.website.notification+json",
                    headers: {
                        Accept: "application/vnd.oneandone.diy.website.notification+json"
                    },
                    success: resolve,
                    error: reject
                });
            } catch (reason) {
                reject(reason);
            }
        });
    }

    /**
     * Creates multiple Notifications with a given state list
     *
     * @param   {NotificationState[]} notificationStateList
     * @returns {Promise<{}>}
     */
    static createNotifications(notificationStateList: NotificationStateList): Promise<{}> {
        return new Promise((resolve, reject) => {
            try {
                let notificationRestData = Array.from(notificationStateList.values())
                    .map(state => RestApi.getNotificationData(state));

                $.ajax({
                    method: "POST",
                    url: this.restUrl,
                    data: JSON.stringify(notificationRestData),
                    contentType: "application/vnd.oneandone.diy.website.notifications+json",
                    headers: {
                        Accept: "application/vnd.oneandone.diy.website.notifications+json"
                    },
                    success: resolve,
                    error: reject
                });
            } catch (reason) {
                reject(reason);
            }
        });
    }

    /**
     * Get a single notification from the actual website by the type
     *
     * @param   {number} type
     * @returns {Promise<NotificationState>}
     */
    static getNotification(type: number): Promise<NotificationState> {
        return new Promise((resolve, reject) => {
            $.ajax({
                method: "GET",
                url: this.restUrl + "/" + type,
                contentType: "application/vnd.oneandone.diy.website.notification+json",
                headers: {
                    Accept: "application/vnd.oneandone.diy.website.notification+json"
                },
                success: (data: NotificationRestData) => {
                    try {
                        resolve(this.convertNotificationData(data));
                    } catch (reason) {
                        reject(reason);
                    }
                },
                error: reject
            });
        });
    }

    /**
     * Returns all notificationStateList for the actual website
     *
     * @returns {Promise<NotificationStateList>}
     */
    static getAllNotifications(): Promise<NotificationStateList> {
        return new Promise((resolve, reject) => {
            $.ajax({
                method: "GET",
                url: this.restUrl,
                contentType: "application/vnd.oneandone.diy.website.notifications+json",
                headers: {
                    Accept: "application/vnd.oneandone.diy.website.notifications+json"
                },
                success: (data: any) => {
                    let notificationStateList = new Map<number, NotificationState>();

                    data.notifications.forEach((notificationRestData: NotificationRestData) => {
                        try {
                            notificationStateList.set(
                                notificationRestData.type,
                                this.convertNotificationData(
                                    notificationRestData
                                )
                            );
                        } catch (reason) {
                            reject(reason);
                        }
                    });
                    resolve(notificationStateList);
                },
                error: reject
            });
        });
    }

    /**
     * Updates a notification in the database with the same type as the type in the given
     * NotificationState object
     *
     * @param   {NotificationState} notificationState
     * @returns {Promise<{}>}
     */
    static updateNotification(notificationState: NotificationState): Promise<{}> {
        return new Promise((resolve, reject) => {
            try {
                $.ajax({
                    method: "PUT",
                    url: this.restUrl,
                    data: JSON.stringify(this.getNotificationData(notificationState)),
                    contentType: "application/vnd.oneandone.diy.website.notification+json",
                    headers: {
                        Accept: "application/vnd.oneandone.diy.website.notification+json"
                    },
                    success: resolve,
                    error: reject
                });
            } catch (reason) {
                reject(reason);
            }
        });
    }

    /**
     * Updates many notificationStateList in the database by the type from the actual processed NotificationState
     * Object from the NotificationState array
     *
     * @param   {NotificationStateList} notificationStateList
     * @returns {Promise<{}>}
     */
    static updateNotifications(notificationStateList: NotificationStateList): Promise<{}> {
        return new Promise((resolve, reject) => {
            try {
                let notificationRestData: Array<NotificationRestData> = [];
                notificationStateList.forEach((notificationState: NotificationState) => {
                    notificationRestData.push(
                        this.getNotificationData(notificationState)
                    );
                });
                $.ajax({
                    method: "PUT",
                    url: this.restUrl,
                    data: JSON.stringify(notificationRestData),
                    contentType: "application/vnd.oneandone.diy.website.notifications+json",
                    headers: {
                        Accept: "application/vnd.oneandone.diy.website.notifications+json"
                    },
                    success: resolve,
                    error: reject
                });
            } catch (reason) {
                reject(reason);
            }
        });
    }

    /**
     * Translates the Configuration data into Back-End data
     *
     * @param   {NotificationState} notificationState
     * @returns {NotificationRestData}
     */
    static getNotificationData(notificationState: NotificationState): NotificationRestData {
        try {
            let notificationConfig = ConfigFactory.factory(
                notificationState
            );
            return {
                category: 1,
                type: notificationConfig.type,
                sticky: notificationConfig.cardOptions.closable ? 0 : 1,
                payload: notificationConfig.payload,
                severity: notificationConfig.severity,
                read: notificationConfig.read ? 1 : 0,
                dismissed: notificationConfig.dismissed ? 1 : 0,
                deleted: notificationConfig.deleted ? 1 : 0
            };

        } catch (reason) {
            throw new Error(reason);
        }
    }

    /**
     * Translates Back-End data into a State
     *
     * @param   {NotificationRestData} notificationRestData
     * @returns {NotificationState}
     */
    static convertNotificationData(notificationRestData: NotificationRestData): NotificationState {
        try {
            return {
                type: notificationRestData.type,
                counter: Date.now(),
                priority: false,
                read: (notificationRestData.read === 1),
                dismissed: (notificationRestData.dismissed === 1),
                deleted: (notificationRestData.deleted === 1),
                payload: notificationRestData.payload
            };

        } catch (reason) {
            throw new Error(reason);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/RestApi.ts
 **/